import CheckSquareIcon from "@assets/svg/CheckSquareIcon";
import CreditCardIcon from "@assets/svg/CreditCardIcon";
import NotebookIcon from "@assets/svg/NotebookIcon";
import ReceiptIcon from "@assets/svg/ReceiptIcon";
import AnimatedNumber from "@components/AnimatedNumber";
import { getBalanceInfo, withDrawBalance } from "@endpoints/stripe.service";
import { CrownIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { NavLink, Outlet } from "react-router-dom";
import { useSnackbar } from "notistack";
import LoadingAnimation from "@assets/svg/LoadingAnimation";
import {
  getPendingEarnings,
  getPotentialEarnings,
  getTotalEarningAndWithdraw,
} from "@endpoints/payment.service";

const tabs = [
  {
    label: "Payments",
    route: "payments",
  },
  {
    label: "Refunds",
    route: "refunds",
  },
  {
    label: "Balance",
    route: "balance",
  },
  {
    label: "Payment methods",
    route: "payment-methods",
  },
];

interface Earnings {
  potentialEarning: number;
  pendingEarning: number;
  totalWithdrawal: number;
  totalEarning: number;
}

const ReferralLayout = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [balance, setBalance] = useState(0);
  const [earnings, setEarnings] = useState({
    potentialEarning: 0,
    pendingEarning: 0,
    totalWithdrawal: 0,
    totalEarning: 0,
  });
  const [isBalanceWithdrawing, setIsBalanceWithdrawing] = useState(false);
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);

  const stats = [
    {
      label: "Potential earnings",
      value: earnings?.potentialEarning,
      icon: <CheckSquareIcon className="w-7 h-7" />,
    },
    {
      label: "Withdrawn so far",
      value: earnings?.totalWithdrawal,
      icon: <CreditCardIcon className="w-7 h-7" />,
    },
    {
      label: "Pending earnings",
      value: earnings?.pendingEarning,
      icon: <NotebookIcon className="w-7 h-7" />,
    },
    {
      label: "Total earnings",
      value: earnings?.totalEarning,
      icon: <CrownIcon className="w-7 h-7" stroke="#2e2e2e" />,
    },
  ];

  async function fetchBalance() {
    await getBalanceInfo()
      .then((response) => {
        if (response?.success) {
          let instantAmount = response.data.instant_available[0].amount;
          if (instantAmount > 0) instantAmount /= 100;
          setBalance(instantAmount);
        }
      })
      .catch((err) => console.error("Error fetching balance", err));
    await getPotentialEarnings()
      .then((response) => {
        if (response?.success) {
          setEarnings((prev) => {
            return {
              ...prev,
              potentialEarning: response?.data[0]?.potentialEarning,
            };
          });
        }
      })
      .catch((err) => console.error("Error fetching balance", err));
    await getPendingEarnings()
      .then((response) => {
        if (response?.success) {
          setEarnings((prev) => {
            return {
              ...prev,
              pendingEarning: response?.data[0]?.pendingEarning,
            };
          });
        }
      })
      .catch((err) => console.error("Error fetching balance", err));
    await getTotalEarningAndWithdraw()
      .then((response) => {
        if (response?.success) {
          setEarnings((prev) => {
            return {
              ...prev,
              totalEarning: response?.data?.totalEarning,
              totalWithdrawal: response?.data?.totalWithdrawal,
            };
          });
        }
      })
      .catch((err) => console.error("Error fetching balance", err));
  }

  async function withdrawBalance(amount: number) {
    setIsBalanceWithdrawing(true);
    await withDrawBalance(amount * 100)
      .then((response) => {
        console.log("withdrawl response", response);
        if (response.success) {
          fetchBalance();
          enqueueSnackbar("Withdrawal requested successfully.", {
            variant: "success",
          });
        }
      })
      .catch((err) => {
        console.error("withdrawl error ", err);
        enqueueSnackbar(err, { variant: "error" });
      })
      .finally(() => setIsBalanceWithdrawing(false));
  }

  const disableWithdraw = balance === 0 || isBalanceWithdrawing;

  useEffect(() => {
    fetchBalance();
  }, []);

  console.log("earnings", earnings);

  return (
    <div className="flex flex-col rounded-md bg-white flex-1 w-full p-5">
      {/* Top container */}
      <div className="flex gap-5">
        <div className="flex flex-col items-center rounded-md bg-[#F1F5F9] p-2 pb-4 gap-5 w-[343px] h-max">
          <div className="flex items-center gap-3 self-start">
            <div className="bg-white rounded-md p-3">
              <ReceiptIcon className="w-7 h-7" />
            </div>
            <div className="flex flex-col gap-2">
              <span className="font-medium text-sm">Available balance</span>
              {isBalanceWithdrawing ? (
                <LoadingAnimation color="#2e2e2e" />
              ) : (
                <span className="font-bold text-lg">
                  £
                  <AnimatedNumber
                    value={balance}
                    className="font-bold text-lg"
                  />
                </span>
              )}
            </div>
          </div>
          <button
            onClick={() => setShowWithdrawModal(true)}
            disabled={disableWithdraw}
            className={`flex items-center justify-center font-semibold text-sm px-14 py-2.5 bg-white rounded-full border border-[#6f6f6f] hover:bg-gray-50 ${
              disableWithdraw ? "opacity-50" : ""
            }`}
          >
            Withdraw Now
          </button>
        </div>
        <div className="grid grid-cols-2 gap-3 flex-1">
          {stats?.map((stat) => {
            return (
              <div className="flex items-center bg-[#F1F5F9] h-max rounded-md p-2 flex-1 gap-3">
                <div className="bg-white rounded-md p-2">{stat?.icon}</div>
                <div className="flex flex-col">
                  <span className="font-medium text-sm">{stat?.label}</span>
                  {/* <span className="font-bold text-lg">£12,500</span> */}
                  <span className="font-bold text-lg">
                    £
                    <AnimatedNumber
                      value={stat?.value || 0}
                      className="font-bold text-lg"
                    />
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      {/* Tab container */}
      <div className="flex items-center gap-8 mt-5 border-b border-gray-300 pb-2 px-3">
        {tabs?.map((tab) => {
          return (
            <NavLink key={tab?.label} to={tab?.route}>
              {({ isActive }) => (
                <span
                  className={`font-semibold text-xs ${
                    isActive ? "border-b text-primary" : "text-[#bababa]"
                  } border-primary pb-0.5`}
                >
                  {tab?.label}
                </span>
              )}
            </NavLink>
          );
        })}
      </div>
      <Outlet />
    </div>
  );
};
export default ReferralLayout;
